let pdfLibLoaderPromise = null;

const PDF_TEXT_REPLACEMENTS = new Map([
  ['\u03B8', 'theta'],
  ['\u0398', 'Theta'],
  ['\u03B1', 'alpha'],
  ['\u03B2', 'beta'],
  ['\u03B3', 'gamma'],
  ['\u03B4', 'delta'],
  ['\u0394', 'Delta'],
  ['\u03BB', 'lambda'],
  ['\u03BC', 'mu'],
  ['\u03C0', 'pi'],
  ['\u03A0', 'Pi'],
  ['\u03C3', 'sigma'],
  ['\u03A3', 'Sigma'],
  ['\u03C6', 'phi'],
  ['\u03A6', 'Phi'],
  ['\u03C9', 'omega'],
  ['\u03A9', 'Omega'],
  ['\u2264', '<='],
  ['\u2265', '>='],
  ['\u2260', '!='],
  ['\u2248', '~='],
  ['\u221E', 'infinity'],
  ['\u221A', 'sqrt'],
  ['\u00B0', ' deg'],
  ['\u2013', '-'],
  ['\u2014', '-'],
  ['\u2212', '-'],
  ['\u00D7', 'x'],
  ['\u00F7', '/'],
  ['\u2019', "'"],
  ['\u2018', "'"],
  ['\u201C', '"'],
  ['\u201D', '"'],
  ['\u2022', '-'],
  ['\u2026', '...']
]);

const ensurePdfLib = () => {
  if (window.PDFLib) return Promise.resolve(window.PDFLib);
  if (pdfLibLoaderPromise) return pdfLibLoaderPromise;

  pdfLibLoaderPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-pdflib-loader="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.PDFLib), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load PDF library.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/dist/pdf-lib.min.js';
    script.async = true;
    script.dataset.pdflibLoader = 'true';
    script.onload = () => {
      if (!window.PDFLib) {
        reject(new Error('PDF library unavailable.'));
        return;
      }
      resolve(window.PDFLib);
    };
    script.onerror = () => reject(new Error('Failed to load PDF library.'));
    document.body.appendChild(script);
  });

  return pdfLibLoaderPromise;
};

const sanitizePdfText = (value, fallback = '') => {
  const source = String(value ?? fallback ?? '');
  const normalized = source.normalize('NFKD');
  let sanitized = '';

  for (const char of normalized) {
    if (PDF_TEXT_REPLACEMENTS.has(char)) {
      sanitized += PDF_TEXT_REPLACEMENTS.get(char);
      continue;
    }

    const codePoint = char.codePointAt(0) || 0;
    const isAsciiPrintable = codePoint === 10 || codePoint === 13 || (codePoint >= 32 && codePoint <= 126);
    if (isAsciiPrintable) sanitized += char;
  }

  return sanitized.replace(/\s+/g, ' ').trim() || String(fallback ?? '');
};

const clampPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric));
};

const hexToRgbComponents = (value) => {
  const match = String(value || '').trim().match(/^#([0-9a-f]{6})$/i);
  if (!match) return [0.9, 0.12, 0.12];
  const numeric = Number.parseInt(match[1], 16);
  return [
    ((numeric >> 16) & 255) / 255,
    ((numeric >> 8) & 255) / 255,
    (numeric & 255) / 255
  ];
};

const toPercentPoint = (point = {}) => ({
  xPercent: clampPercent(point.xPercent ?? point.x),
  yPercent: clampPercent(point.yPercent ?? point.y)
});

const getManualMarkerLayout = (pageWidth, pageHeight) => {
  const baseScale = Math.min(pageWidth, pageHeight) / 595;
  const scale = Math.max(0.45, Math.min(0.75, baseScale));

  return {
    scale,
    tickThickness: 2.8 * scale,
    tickShort: 10 * scale,
    tickMid: 15 * scale,
    tickLong: 24 * scale,
    crossHalf: 10 * scale,
    halfTickStart: 4 * scale,
    halfTickEndX: 12 * scale,
    halfTickEndY: 8 * scale,
    halfTickThickness: 2.2 * scale
  };
};

const drawMarkerSymbol = (page, ann, pageWidth, pageHeight, layout, colors) => {
  const pdfX = (clampPercent(ann.xPercent) / 100) * pageWidth;
  const pdfY = pageHeight - (clampPercent(ann.yPercent) / 100) * pageHeight;
  const {
    tickThickness,
    tickShort,
    tickMid,
    tickLong,
    crossHalf,
    halfTickStart,
    halfTickEndX,
    halfTickEndY,
    halfTickThickness
  } = layout;

  if (ann.type === 'tick') {
    page.drawLine({
      start: { x: pdfX - tickShort, y: pdfY },
      end: { x: pdfX, y: pdfY - tickMid },
      thickness: tickThickness,
      color: colors.tick
    });
    page.drawLine({
      start: { x: pdfX, y: pdfY - tickMid },
      end: { x: pdfX + tickLong, y: pdfY + tickMid },
      thickness: tickThickness,
      color: colors.tick
    });
    return;
  }

  if (ann.type === 'cross') {
    page.drawLine({
      start: { x: pdfX - crossHalf, y: pdfY - crossHalf },
      end: { x: pdfX + crossHalf, y: pdfY + crossHalf },
      thickness: tickThickness,
      color: colors.cross
    });
    page.drawLine({
      start: { x: pdfX - crossHalf, y: pdfY + crossHalf },
      end: { x: pdfX + crossHalf, y: pdfY - crossHalf },
      thickness: tickThickness,
      color: colors.cross
    });
    return;
  }

  if (ann.type === 'half-tick') {
    page.drawLine({
      start: { x: pdfX - tickShort, y: pdfY },
      end: { x: pdfX, y: pdfY - tickMid },
      thickness: tickThickness,
      color: colors.halfTick
    });
    page.drawLine({
      start: { x: pdfX, y: pdfY - tickMid },
      end: { x: pdfX + tickLong, y: pdfY + tickMid },
      thickness: tickThickness,
      color: colors.halfTick
    });
    page.drawLine({
      start: { x: pdfX - halfTickStart, y: pdfY + halfTickStart },
      end: { x: pdfX + halfTickEndX, y: pdfY - halfTickEndY },
      thickness: halfTickThickness,
      color: colors.halfTick
    });
  }
};

const wrapPdfText = (text, font, fontSize, maxWidth) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const lines = [];
  let currentLine = words[0];

  for (let index = 1; index < words.length; index += 1) {
    const candidate = `${currentLine} ${words[index]}`;
    if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
      currentLine = candidate;
    } else {
      lines.push(currentLine);
      currentLine = words[index];
    }
  }

  lines.push(currentLine);
  return lines;
};

const drawStickyNote = (page, ann, pageWidth, pageHeight, fonts, layout, colors) => {
  const noteX = (clampPercent(ann.xPercent) / 100) * pageWidth;
  const noteY = pageHeight - (clampPercent(ann.yPercent) / 100) * pageHeight;
  const titleSize = Math.max(8, 10 * layout.scale);
  const bodySize = Math.max(7, 8.5 * layout.scale);
  const horizontalPadding = 6 * layout.scale;
  const verticalPadding = 5 * layout.scale;
  const lineGap = 3 * layout.scale;
  const maxWidth = 96 * layout.scale;

  const titleText = sanitizePdfText(
    `Q${ann.questionLabel || '-'} ${Number(ann.marksAwarded || 0)}/${Number(ann.marksAvailable || 0)}`,
    'Q- 0/0'
  );
  const commentText = sanitizePdfText(ann.comment || '', '');
  const commentLines = commentText
    ? wrapPdfText(commentText, fonts.regular, bodySize, maxWidth - (horizontalPadding * 2)).slice(0, 3)
    : [];

  const titleHeight = titleSize;
  const bodyHeight = commentLines.length > 0
    ? (commentLines.length * bodySize) + ((commentLines.length - 1) * lineGap)
    : 0;
  const noteHeight = verticalPadding + titleHeight + (commentLines.length > 0 ? lineGap + bodyHeight : 0) + verticalPadding;
  const clampedX = Math.max(8, Math.min(pageWidth - maxWidth - 8, noteX - (maxWidth / 2)));
  const clampedY = Math.max(8, Math.min(pageHeight - noteHeight - 8, noteY - (noteHeight / 2)));

  page.drawRectangle({
    x: clampedX,
    y: clampedY,
    width: maxWidth,
    height: noteHeight,
    color: colors.noteFill,
    borderColor: colors.noteBorder,
    borderWidth: Math.max(1, 1.2 * layout.scale),
    opacity: 0.95
  });

  page.drawText(titleText, {
    x: clampedX + horizontalPadding,
    y: clampedY + noteHeight - verticalPadding - titleSize,
    size: titleSize,
    font: fonts.bold,
    color: colors.noteText
  });

  commentLines.forEach((line, index) => {
    page.drawText(line, {
      x: clampedX + horizontalPadding,
      y: clampedY + noteHeight - verticalPadding - titleSize - lineGap - bodySize - (index * (bodySize + lineGap)),
      size: bodySize,
      font: fonts.regular,
      color: colors.noteText
    });
  });
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error('Failed to read image.'));
  reader.readAsDataURL(blob);
});

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Failed to decode image.'));
  img.src = src;
});

const convertImageToPngBytes = async (bytes, mimeType) => {
  const dataUrl = await blobToDataUrl(new Blob([bytes], { type: mimeType || 'image/webp' }));
  const img = await loadImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas renderer unavailable.');
  ctx.drawImage(img, 0, 0);
  const pngBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!pngBlob) throw new Error('Failed to convert image.');
  return new Uint8Array(await pngBlob.arrayBuffer());
};

const fetchFileBytes = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download file (${response.status}).`);
  }
  return new Uint8Array(await response.arrayBuffer());
};

export const generateManualMarkedPdf = async ({ scriptFiles, resultData, resolveDownloadUrl }) => {
  if (!Array.isArray(scriptFiles) || scriptFiles.length === 0) {
    throw new Error('No script files available for manual marking.');
  }
  if (typeof resolveDownloadUrl !== 'function') {
    throw new Error('A download URL resolver is required.');
  }

  const PDFLib = await ensurePdfLib();
  const { PDFDocument, StandardFonts, rgb } = PDFLib;

  const scriptFilesData = [];
  for (const entry of scriptFiles) {
    const url = await resolveDownloadUrl(entry.path);
    const bytes = await fetchFileBytes(url);
    scriptFilesData.push({
      bytes,
      mimeType: String(entry.mimeType || '').toLowerCase()
    });
  }

  const scriptIsPdf = (scriptFiles[0]?.mimeType || '').includes('pdf');
  let scriptPdf;
  if (scriptIsPdf) {
    scriptPdf = await PDFDocument.load(scriptFilesData[0].bytes);
  } else {
    scriptPdf = await PDFDocument.create();
    for (const imgData of scriptFilesData) {
      const mime = imgData.mimeType;
      let imageBytes = imgData.bytes;
      let embeddedImage;

      if (mime.includes('webp')) {
        imageBytes = await convertImageToPngBytes(imgData.bytes, mime);
        embeddedImage = await scriptPdf.embedPng(imageBytes);
      } else if (mime.includes('png')) {
        embeddedImage = await scriptPdf.embedPng(imageBytes);
      } else {
        embeddedImage = await scriptPdf.embedJpg(imageBytes);
      }

      const { width, height } = embeddedImage.scale(1);
      const page = scriptPdf.addPage([width, height]);
      page.drawImage(embeddedImage, { x: 0, y: 0, width, height });
    }
  }

  const font = await scriptPdf.embedFont(StandardFonts.HelveticaBold);
  const commentFont = await scriptPdf.embedFont(StandardFonts.Helvetica);
  const colors = {
    tick: rgb(0.90, 0.12, 0.12),
    cross: rgb(0.90, 0.12, 0.12),
    halfTick: rgb(0.93, 0.58, 0.08),
    total: rgb(0.85, 0.10, 0.10),
    noteFill: rgb(0.99, 0.94, 0.55),
    noteBorder: rgb(0.96, 0.72, 0.12),
    noteText: rgb(0.15, 0.18, 0.22)
  };

  const pageResults = Array.isArray(resultData?.pageResults) ? resultData.pageResults : [];
  const iconResults = Array.isArray(resultData?.markerIcons) ? resultData.markerIcons : [];
  const penStrokeResults = Array.isArray(resultData?.penStrokes) ? resultData.penStrokes : [];
  const pageCount = scriptPdf.getPageCount();

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    const page = scriptPdf.getPage(pageIndex);
    const { width, height } = page.getSize();
    const layout = getManualMarkerLayout(width, height);

    const annotationEntries = (pageResults.find((pr) => pr.pageNumber === (pageIndex + 1))?.annotations || [])
      .map((ann) => ({ ...ann, ...toPercentPoint(ann) }));
    annotationEntries.forEach((ann) => {
      drawStickyNote(page, ann, width, height, { bold: font, regular: commentFont }, layout, colors);
    });

    const iconEntries = (iconResults.find((pr) => pr.pageNumber === (pageIndex + 1))?.icons || [])
      .map((icon) => ({ ...icon, ...toPercentPoint(icon) }));
    iconEntries.forEach((icon) => {
      const markerColor = rgb(...hexToRgbComponents(icon.color));
      drawMarkerSymbol(page, icon, width, height, layout, {
        ...colors,
        tick: markerColor,
        cross: markerColor
      });
    });

    const drawingEntries = penStrokeResults.find((entry) => entry.pageNumber === (pageIndex + 1))?.strokes || [];
    drawingEntries.forEach((stroke) => {
      const points = Array.isArray(stroke.points) ? stroke.points : [];
      const strokeColor = rgb(...hexToRgbComponents(stroke.color));
      for (let pointIndex = 1; pointIndex < points.length; pointIndex += 1) {
        const start = points[pointIndex - 1];
        const end = points[pointIndex];
        page.drawLine({
          start: {
            x: (clampPercent(start.xPercent) / 100) * width,
            y: height - ((clampPercent(start.yPercent) / 100) * height)
          },
          end: {
            x: (clampPercent(end.xPercent) / 100) * width,
            y: height - ((clampPercent(end.yPercent) / 100) * height)
          },
          thickness: Math.max(1.5, 2.2 * layout.scale),
          color: strokeColor
        });
      }
    });
  }

  const totalScore = Number(resultData?.totalScore || 0);
  const maxScore = Number(resultData?.maxScore || 0);
  const safePercentage = maxScore > 0
    ? Math.round((totalScore / maxScore) * 100)
    : Number(resultData?.percentage || 0);

  const firstPage = scriptPdf.getPage(0);
  const { width: firstPageWidth, height: firstPageHeight } = firstPage.getSize();
  const totalText = sanitizePdfText(
    `Total: ${totalScore}/${maxScore} (${safePercentage}%)`,
    'Total: 0/0 (0%)'
  );
  const totalScale = Math.max(0.45, Math.min(0.9, Math.min(firstPageWidth, firstPageHeight) / 595));
  const totalSize = 22 * totalScale;
  const totalWidth = font.widthOfTextAtSize(totalText, totalSize);
  firstPage.drawText(totalText, {
    x: Math.max(18 * totalScale, firstPageWidth - totalWidth - (24 * totalScale)),
    y: firstPageHeight - (34 * totalScale),
    size: totalSize,
    font,
    color: colors.total
  });

  return scriptPdf.save();
};
